import React, { useEffect, useState } from "react";
import {
    Box, Chip, CircularProgress,
    Paper, Stack, Table, TableBody, TableCell, TableHead,
    TableRow, Typography
} from "@mui/material";
import { ds } from "../../../styles/designSystem";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import { channelApi } from "../api/channelApi";
import { notify } from "../../../util/toast";
import LoadingDots from "../../../component/common/LoadingDots";
import ComboBox from "../../../component/common/ComboBox";
import ResizableTable from "../../../component/common/ResizableTable";
import TableToolbar from "../../../component/common/TableToolbar";
import { useTableSearch } from "../../../util/tableSearch";
import dayjs from "dayjs";
import { formatFileSize } from "../../../util/numberFormat";


export default function FileTab({ channelId, members,
                                 /** 조회 범위 — {} (전체) | { folderId } | { onlyUnfiled: true }.
                                  *  첨부에는 폴더 컬럼이 없어 서버가 부모(피드·댓글·업무)의 폴더로 판정한다. */
                                 folderScope = {} }) {
    /**
     * 폴더 범위를 useEffect 의존성으로 쓰기 위한 문자열 키 —
     * folderScope 는 매 렌더 새 객체라 그대로 넣으면 무한 재조회가 된다.
     */
    const folderKey = JSON.stringify(folderScope);

    const [files, setFiles] = useState([]);
    // 표 안 검색 — 공통 유틸이 행 전체를 훑는다.
    const [tableQuery, setTableQuery] = useState("");
    const filesFiltered = useTableSearch(files, tableQuery);
    const [loading, setLoading] = useState(true);
    const [filterUploader, setFilterUploader] = useState("");

    const load = async (uploaderId) => {
        setLoading(true);
        try {
            const res = await channelApi.getFiles(channelId, uploaderId || undefined, folderScope);
            setFiles(Array.isArray(res) ? res : (res?.files ?? []));
        } catch {
            notify("파일 목록 불러오기 실패", "error");
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [channelId, folderKey]);

    return (
        <Box>
            {/* 필터 */}
            <Stack direction="row" spacing={2} mb={2} alignItems="center">
                <Box sx={{ width: 180 }}>
                    <ComboBox placeholder="게시자 필터 · 전체"
                        options={[{ value: "", label: "전체" }, ...(members || []).map(m => ({ value: m.employee.id, label: m.employee.name }))]}
                        value={filterUploader}
                        onChange={v => { setFilterUploader(v); load(v || undefined); }} />
                </Box>
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    총 {files.length}개 파일
                </Typography>
                {/* 검색 — 우측 상단 고정(전 시스템 공통 배치). 이 표엔 동작 버튼이 없어 검색이 맨 오른쪽. */}
                <TableToolbar onSearch={setTableQuery} placeholder="파일명·작성자 검색"
                    sx={{ flex: 1, mb: 0, minWidth: 200 }} />
            </Stack>

            {loading ? (
                <LoadingDots />
            ) : files.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                    <Typography>업로드된 파일이 없습니다.</Typography>
                </Box>
            ) : (
                <Paper sx={{ borderRadius: 1.5, border: "1px solid rgba(0,0,0,0.08)", overflow: "hidden" }}>
                    <ResizableTable size="small" sx={ds.tableSx}>
                        <TableHead>
                            <TableRow>
                                <TableCell>파일명</TableCell>
                                <TableCell>크기</TableCell>
                                <TableCell>구분</TableCell>
                                <TableCell>업로더</TableCell>
                                <TableCell>업로드 시각</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filesFiltered.map(f => (
                                <TableRow key={f.id} hover>
                                    <TableCell>
                                        <Stack direction="row" alignItems="center" spacing={0.75}>
                                            <InsertDriveFileRoundedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                            <Typography
                                                component="a"
                                                href={f.fileUrl}
                                                target="_blank"
                                                sx={{ fontSize: 13, color: "primary.main", textDecoration: "none",
                                                    "&:hover": { textDecoration: "underline" } }}
                                            >
                                                {f.originalFileName}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 13 }}>{formatFileSize(f.fileSize, "-")}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={f.feedId ? "피드" : f.commentId ? "댓글" : "-"}
                                            size="small"
                                            sx={{ fontSize: 11 }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 13 }}>{f.uploader?.name ?? "-"}</TableCell>
                                    <TableCell sx={{ fontSize: 13 }}>
                                        {f.uploadedAt ? dayjs(f.uploadedAt).format("YYYY.MM.DD HH:mm") : "-"}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </ResizableTable>
                </Paper>
            )}
        </Box>
    );
}